/**
 * Token Refresh Service
 * Gerencia renovação automática de tokens JWT
 */

import { authService } from './authService';

class TokenRefreshService {
  private refreshInterval: NodeJS.Timeout | null = null;
  private readonly CHECK_INTERVAL = 60000; // Verificar a cada 1 minuto
  private readonly REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000; // Renovar 5 minutos antes de expirar
  private isRefreshing = false;

  /**
   * Iniciar monitoramento de expiração do token
   */
  start(): void {
    if (this.refreshInterval) {
      return;
    }

    console.log('🔄 Token refresh service started');

    // Verificar imediatamente
    this.checkAndRefreshToken();

    // Verificar periodicamente
    this.refreshInterval = setInterval(() => {
      this.checkAndRefreshToken();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Parar monitoramento
   */
  stop(): void {
    console.log('🛑 Token refresh service stopped');

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Verificar se token precisa ser renovado
   */
  private async checkAndRefreshToken(): Promise<void> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!accessToken || !refreshToken) {
        console.log('🔒 No tokens found - skipping refresh check');
        return;
      }

      // Decodificar token para verificar expiração
      const tokenData = this.decodeToken(accessToken);

      if (!tokenData || !tokenData.exp) {
        console.warn('⚠️ Invalid token format');
        return;
      }

      const expiresAt = tokenData.exp * 1000; // Converter para ms
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // Log de debug
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60000);
      console.log(`⏰ Token expires in ${minutesUntilExpiry} minutes`);

      // Se falta menos de 5 minutos para expirar, renovar
      if (timeUntilExpiry < this.REFRESH_BEFORE_EXPIRY && timeUntilExpiry > 0) {
        console.log('🔄 Token expiring soon - refreshing...');
        await this.refreshTokenNow(refreshToken);
      }
    } catch (error) {
      console.error('❌ Error checking token expiry:', error);
    }
  }

  /**
   * Renovar token imediatamente
   */
  async refreshTokenNow(refreshToken?: string): Promise<boolean> {
    // Prevenir múltiplas renovações simultâneas
    if (this.isRefreshing) {
      console.log('⏳ Refresh already in progress - skipping');
      return false;
    }

    try {
      this.isRefreshing = true;

      const token = refreshToken || localStorage.getItem('refreshToken');

      if (!token) {
        console.warn('⚠️ No refresh token available');
        return false;
      }

      console.log('🔄 Refreshing access token...');

      const tokens = await authService.refreshToken(token);

      // Salvar novos tokens
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);

      console.log('✅ Token refreshed successfully');

      return true;
    } catch (error) {
      console.error('❌ Failed to refresh token:', error);

      // Se refresh falhou, limpar tokens e redirecionar para login
      this.handleRefreshFailure();

      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Tratar falha na renovação
   */
  private handleRefreshFailure(): void {
    console.log('🔒 Refresh token expired or invalid - logging out');

    // Limpar tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Disparar evento para AuthContext
    window.dispatchEvent(new CustomEvent('auth:token-refresh-failed'));
  }

  /**
   * Decodificar JWT (sem verificar assinatura - apenas para ler exp)
   */
  private decodeToken(token: string): { exp?: number; iat?: number } | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
}

// Singleton instance
export const tokenRefreshService = new TokenRefreshService();
